import { clone } from 'ramda';
import { numberPredicate } from 'js/utils/predicate.util';
import { IPaymentAccountField } from 'js/hooks/socket/types';
import { CASHIER_REDIRECT_TYPE, CASHIER_TYPE } from 'js/hooks/socket/constants';
import { IPaymentAccount } from 'js/hooks/socket/hooks/useGetPaymentMethodAccounts/types';

import { WITHDRAW_REDIRECT_OFFER_ACC } from '../constants';
import { parseValidationConfig } from './parseMethodValidationConfig';
import { portalCashierConfig } from '../configs/portalCashierConfig';
import { portalAccountFieldsConfig } from '../configs/portalAccountFieldsConfig';
import {
    IGetAccountParams, IParsedPaymentMethod, IParsedAccount,
    ICashierMethodConfig, IMultiFieldConfig
} from '../types';

export const getPortalCashierConfig = (method: IParsedPaymentMethod, type: CASHIER_TYPE): ICashierMethodConfig => {
    const { code, reDirectType } = method;
    const individualConfig = portalCashierConfig[type][code];
    const isGeneric = Boolean(!individualConfig || individualConfig.isGeneric);
    const isNuveiCC = reDirectType === CASHIER_REDIRECT_TYPE.JS_NUVEI;
    const config = individualConfig || portalCashierConfig[type].generic;

    if (!config.validationConfig) {
        config.validationConfig = portalCashierConfig[type].basic.validationConfig;
    }

    return {
        isGeneric: isGeneric && !isNuveiCC,
        ...config,
        validationConfig: clone(config.validationConfig)
    };
};

export const getMultiFieldConfig = (accountFields: IPaymentAccountField[]): IMultiFieldConfig => {
    if (!accountFields.length) {
        return {};
    }

    const keyFields = accountFields?.filter(field => field.isAccountKey);

    return {
        keyFields,
        isNoAccountKeys: !keyFields.length,
        isMultiFieldKey: keyFields.length > 1
    };
};

export const getLabelFieldConfig = (accountFields: IPaymentAccountField[]): { labelField?: IPaymentAccountField } => {
    const labelField = accountFields?.find(field => field.isLabelField);

    return { ...labelField && { labelField } };
};

export const mergeAccountFields = (
    type: CASHIER_TYPE,
    code: string,
    imsAccountFields: IPaymentAccountField[]
): IPaymentAccountField[] => {
    const portalFields: IPaymentAccountField[] = portalAccountFieldsConfig[type][code] || [];

    switch (code) {
        default:
            return Object.values([...imsAccountFields, ...portalFields].reduce((acc, field) => ({
                ...acc,
                [field.name]: field
            }), {}));
    }
};

/** Documentation for generic & custom account fields:
 *
 * To apply automatically accountFields' logic for "custom" account fields:
 * 1. they should be added into "portalAccountFieldsConfig"
 * 2. "mergeAccountFields" can override IMS accountFields or add custom fields
 * 3. accountField only has "required" and "regexp" validation, to add another type "portalCashierConfig" can be used
 *
 * If "CustomAccountSection" has some other account fields, they can be handled in:
 * 1. Form's initialValues & validationConfig
 * 2. useCustomSecureFields - if field should be cleared after submit
 * */
export const getAccountFields = ({ type, code, reDirectType, imsAccountFields }: {
    type: CASHIER_TYPE; code: string; reDirectType: CASHIER_REDIRECT_TYPE; imsAccountFields: IPaymentAccountField[];
}): IPaymentAccountField[] => {
    const { accountFieldsMap } = portalCashierConfig[type][code] || {};
    const accountFields = mergeAccountFields(type, code, imsAccountFields);
    const isWithdrawPaySafeField = type === CASHIER_TYPE.WITHDRAWAL
        && reDirectType === CASHIER_REDIRECT_TYPE.JS_PAY_SAFE;

    return accountFields.map(field => {
        const isHiddenField = field.isLabelField && !field.isAccountKey && !field.isRequired; // COR18-6105

        return {
            ...field,
            ...accountFieldsMap?.[field.name] && { name: accountFieldsMap[field.name] },
            isHiddenField: isHiddenField || isWithdrawPaySafeField
        };
    });
};

export const getAccountDepositTime = (account: IPaymentAccount): number =>
    account.lastDepositTime ? new Date(account.lastDepositTime).getTime() : 0;

export const getAccountWithdrawTime = (account: IPaymentAccount): number =>
    account.lastWithdrawTime ? new Date(account.lastWithdrawTime).getTime() : 0;

export const getAccounts = ({
    type, account, labelField, keyFields
}: IGetAccountParams): IParsedAccount[] => {
    if (!account) {
        return [];
    }

    const compareFn = numberPredicate(
        false, type === CASHIER_TYPE.DEPOSIT ? getAccountDepositTime : getAccountWithdrawTime
    );

    const result = account.map(acc => {
        let title = '';
        const { specificParams } = acc;

        if (labelField) {
            const labelFieldTitle = specificParams?.[labelField.name as keyof typeof specificParams];

            title = typeof labelFieldTitle === 'string' ? labelFieldTitle : '';
        } else if (keyFields?.length) {
            title = keyFields
                .map(keyField => {
                    const keyFieldTitle = specificParams?.[keyField.name as keyof typeof specificParams];

                    return typeof keyFieldTitle === 'string' ? keyFieldTitle : '';
                })
                ?.join('/') || '';
        }

        return { ...acc, title };
    });

    result.sort(compareFn);

    return result;
};

export const parsePaymentMethod = (
    method: IParsedPaymentMethod,
    type: CASHIER_TYPE
): IParsedPaymentMethod => {
    const { account, ...restMethod } = method;
    const portalConfig = getPortalCashierConfig(method, type);
    const accountFields = getAccountFields({
        type,
        code: method.code,
        reDirectType: method.reDirectType,
        imsAccountFields: method.accountFields?.field || []
    });
    const labelFieldConfig = getLabelFieldConfig(accountFields);
    const { keyFields, ...multiFieldConfig } = getMultiFieldConfig(accountFields);
    const defaultSuggestion = method.suggested?.suggestion
        ?.find(suggestion => suggestion['_default'])?.value;

    const parsedMethod: IParsedPaymentMethod = {
        ...restMethod,
        ...portalConfig,
        ...multiFieldConfig,
        ...labelFieldConfig,
        ...keyFields && { keyFields },
        accountFields: {
            field: accountFields
        },
        ...method.suggested && defaultSuggestion && {
            suggested: { ...method.suggested, defaultSuggestion }
        },
        ...account && {
            account: getAccounts({ type, account, labelField: labelFieldConfig.labelField, keyFields })
        },
        ...method.withdrawType === WITHDRAW_REDIRECT_OFFER_ACC && {
            reDirectType: CASHIER_REDIRECT_TYPE.REDIRECT
        }
    };

    return parseValidationConfig(parsedMethod);
};
